import { useState, useEffect, useCallback } from "react";
import Marquee from "react-fast-marquee";
import { motion, AnimatePresence } from "framer-motion";
import { IMG } from "@/lib/content";

const PHOTOS = [
  { src: IMG.cabin, alt: "Cabina in mogano de La Rondine" },
  { src: IMG.saloon, alt: "Salone interno de La Rondine" },
  { src: IMG.deck, alt: "Coperta in legno de La Rondine" },
  { src: IMG.rondineBreakfast, alt: "Colazione a bordo de La Rondine" },
  { src: IMG.rondineCabin2, alt: "Cabina de La Rondine" },
  { src: IMG.rondinellaCruise, alt: "Rondinella in crociera" },
  { src: IMG.rondinellaAperitivo, alt: "Aperitivo al tramonto sulla Rondinella" },
  { src: IMG.rondinellaSunset, alt: "Tramonto in mare con la Rondinella" },
];

export const Gallery = () => {
  const [active, setActive] = useState(null);
  const isOpen = active !== null;

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(
    () => setActive((i) => (i - 1 + PHOTOS.length) % PHOTOS.length),
    []
  );
  const next = useCallback(
    () => setActive((i) => (i + 1) % PHOTOS.length),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, close, prev, next]);

  return (
    <section className="bg-canvas pt-24 md:pt-32" data-testid="gallery">
      <Marquee speed={40} gradient={false} autoFill pauseOnHover>
        {PHOTOS.map((p, i) => (
          <button
            type="button"
            key={p.src + i}
            onClick={() => setActive(i)}
            className="group mx-3 block overflow-hidden rounded-sm border border-gold/30 focus:outline-none focus:ring-2 focus:ring-gold"
            data-testid={`gallery-photo-${i}`}
            aria-label={`Apri foto: ${p.alt}`}
          >
            <img
              src={p.src}
              alt={p.alt}
              loading="lazy"
              className="h-64 md:h-80 w-auto object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </button>
        ))}
      </Marquee>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-navy/95 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button
              type="button"
              onClick={close}
              data-testid="lightbox-close"
              aria-label="Chiudi"
              className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-full border border-gold text-goldsoft transition-colors hover:bg-gold hover:text-navy"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 stroke-current" fill="none" strokeWidth="2" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
            </button>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="lightbox-prev"
              aria-label="Foto precedente"
              className="absolute left-4 md:left-8 flex h-12 w-12 items-center justify-center rounded-full border border-gold text-goldsoft transition-colors hover:bg-gold hover:text-navy"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6 stroke-current" fill="none" strokeWidth="2" aria-hidden="true">
                <path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="lightbox-next"
              aria-label="Foto successiva"
              className="absolute right-4 md:right-8 flex h-12 w-12 items-center justify-center rounded-full border border-gold text-goldsoft transition-colors hover:bg-gold hover:text-navy"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6 stroke-current" fill="none" strokeWidth="2" aria-hidden="true">
                <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <motion.figure
              key={active}
              className="mx-16 md:mx-24 flex max-h-[85vh] max-w-[90vw] flex-col items-center"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={PHOTOS[active].src}
                alt={PHOTOS[active].alt}
                data-testid="lightbox-image"
                className="max-h-[75vh] w-auto rounded-sm border border-gold/40 object-contain"
              />
              <figcaption className="mt-4 text-sm text-canvas/70">
                {active + 1} / {PHOTOS.length}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
